// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { TooltipHost, DefaultButton } from 'office-ui-fabric-react';
import { resetOnStartOver } from './../../store/action';
import { setHiddenAnnouncement } from './../../store/hiddenAnnouncement';
import { logPageAction } from 'src/lib/ApplicationInsights';
import history from 'src/history';
import tooltipIcon from './../../assets/tooltip-copy-3@2x.png';
import Styles from './styles.module.scss';

import SDTResultsModal from './../../components/SDTResultsModal/SDTResultsModal';

interface PropsTypes {
    isCreateOrderView: boolean;
    announceTitle?: boolean;
    claimOrderNumber?: string;
    country: string;
    description: string;
    deviceName: string;
    image: string;
    marketPlace: string;
    partNumber: string;
    resetPath: string;
    serialNumber: string;
    seriesName: string;
    status: string;
    orderType?: boolean;
    zipCode?: string;
    countryName?: string;
    deviceSDTRunDetail?: DeviceSDTRunDetail;
    countryOrigin?: string;
    color?: string;
}

interface DeviceSDTRunDetail {
    sdtRunId: string;
    lastSDTRunDateTime: string;
    testResults: { key: any; value: any; }[];
}

export default function DeviceInformation(props: PropsTypes) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    React.useEffect(() => {
        // announce page title on render
        dispatch(setHiddenAnnouncement({
            message: props.deviceName,
        }));
    }, []);

    function getFormattedSDTDateTime(inputDate?: string) {
        if (inputDate === undefined || inputDate === null || inputDate === '') {
            return '';
        }
        const datePart = inputDate.split('T')[0].split('-');
        const timePart = inputDate.split('T')[1]?.split('.')[0]?.split(':');
        let resultDate = `${datePart[0]}-`;
        resultDate += datePart[1].length > 1 ? `${datePart[1]}-` : `0${datePart[1]}-`;
        resultDate += datePart[2].length > 1 ? `${datePart[2]} ` : `0${datePart[2]} `;

        resultDate += timePart[0].length > 1 ? `${timePart[0]}:` : `0${timePart[0]}:`;
        resultDate += timePart[1].length > 1 ? `${timePart[1]}:` : `0${timePart[1]}:`;
        resultDate += timePart[2].length > 1 ? `${timePart[2]}` : `0${timePart[2]}`;
        return resultDate;
    }

    return (
        <section className={Styles.container}>
            <h1 className={Styles.headerTitle}>{props.deviceName}</h1>
            {RenderHeadSection(props)}
            <div className={Styles.deviceContainer}>
                <div className={Styles.deviceDetailsSection}>
                    <img
                        alt={t('device_image')}
                        className={Styles.deviceImage}
                        src={props.image}
                    />
                    <div className={Styles.details}>
                        <RenderDataSection label={t('device_marketplace')} data={props.marketPlace} />
                        <RenderDataSection label={t('description')} data={props.description} />
                        <RenderDataSection label={t('device_status')} data={props.status} />
                        <div className={props.isCreateOrderView === undefined || props.isCreateOrderView === null || props.isCreateOrderView === false ? Styles.hidden : ''}>
                            <RenderDataSection
                                label={t('last_sdt_run')}>
                                <span className={props.deviceSDTRunDetail === undefined || props.deviceSDTRunDetail === null ? '' : Styles.hidden}>
                                    {t('last_sdt_run_not_available')}
                                </span>
                                <span className={props.deviceSDTRunDetail === undefined || props.deviceSDTRunDetail === null ? Styles.hidden : ''}>
                                    {getFormattedSDTDateTime(props.deviceSDTRunDetail?.lastSDTRunDateTime)}
                                </span>
                                <div className={props.deviceSDTRunDetail === undefined || props.deviceSDTRunDetail === null ? Styles.hidden : ''}>
                                    <TooltipHost
                                        aria-label={t('last_sdt_run_date')}
                                        calloutProps={{ gapSpace: 0 }}
                                        content={t('last_sdt_run_date')}
                                        id="sdt_run">
                                        <img
                                            alt={t('last_sdt_run_date')}
                                            aria-label={t('last_sdt_run_date')}
                                            className={Styles.tooltip}
                                            src={tooltipIcon}
                                            tabIndex={0}
                                            aria-describedby={t('last_sdt_run')}
                                        />
                                    </TooltipHost>
                                </div>
                                <span className={props.deviceSDTRunDetail === undefined || props.deviceSDTRunDetail === null ? Styles.hidden : ''}>
                                    <SDTResultsModal testResults={props.deviceSDTRunDetail?.testResults} />
                                </span>
                            </RenderDataSection>
                        </div>
                    </div>
                </div>
                <div className={Styles.details}>
                    <RenderDataSection label={t('part_number')} data={props.partNumber} />
                    <RenderDataSection label={t('series_name')} data={props.seriesName} />
                    {props.orderType && <RenderDataSection label={t('serial_number')} data={props.serialNumber} />}
                    {<RenderDataSection label={t('country_origin')} data={props.countryOrigin} />}
                    {<RenderDataSection label={t('device_color')} data={props.color} />}
                </div>
            </div>
        </section>
    );
}

function RenderDataSection(props: any) {
    const { t } = useTranslation();
    return (
        <div className={Styles.dataSection}>
            <label className={Styles.title}>
                {props.label}:
            </label>
            <div className={Styles.values}>
                {props.data}
                {props.children}
            </div>
            {props.label === t('country_origin') &&
                <TooltipHost
                    aria-label={t('country_origin')}
                    calloutProps={{ gapSpace: 0 }}
                    content={t('country_origin_tooltip')}
                    id="country_origin"
                >
                    <img
                        alt={t('country_origin')}
                        aria-label={t('country_origin')+t('country_origin_tooltip')}
                        className={Styles.tooltip}
                        src={tooltipIcon}
                        tabIndex={0}
                        aria-describedby="country_origin"
                    />
                </TooltipHost>
            }
        </div>
    );
}

function RenderHeadSection(props: PropsTypes) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    function onStartOver() {
        logPageAction('Start Over button clicked');
        dispatch(resetOnStartOver());
        history.push(props.resetPath);
    }

    return (
        <div className={Styles.headSection}>
            {props.orderType && <RenderDataSection label={t('order_number')} data={props.claimOrderNumber} />}
            {!props.orderType && <RenderDataSection label={t('serial_number')} data={props.serialNumber} />}
            {!props.orderType && <RenderDataSection label={t('country_region')} data={props.country} />}
            {!props.orderType && props.zipCode && <RenderDataSection label={t('zip_code')} data={props.zipCode}>
                <TooltipHost
                    aria-label={t('zip_code_is_only_required_for_service_order_creation')}
                    calloutProps={{ gapSpace: 0 }}
                    content={t('zip_code_is_only_required_for_service_order_creation')}
                    id="zip_code"
                >
                    <img
                        alt={t('zip_code_is_only_required_for_service_order_creation')}
                        aria-label={t('zip_code_is_only_required_for_service_order_creation')}
                        className={Styles.tooltip}
                        src={tooltipIcon}
                        tabIndex={0}
                        aria-describedby="zip_code"
                    />
                </TooltipHost>
            </RenderDataSection>}
            <div className={Styles.buttonContainer}>
                <DefaultButton
                    className={Styles.startOver}
                    text={t('start_over')}
                    onClick={onStartOver}
                    id="startOverBtn"
                />
                <TooltipHost
                    aria-label={t(
                        'this_will_take_you_back_to_the_home_screen'
                    )}
                    calloutProps={{ gapSpace: 0 }}
                    content={t(
                        'this_will_take_you_back_to_the_home_screen'
                    )}
                    id="start_over"
                >
                    <img
                        alt={t('this_will_take_you_back_to_the_home_screen')}
                        aria-label={t('this_will_take_you_back_to_the_home_screen')}
                        className={Styles.tooltip}
                        src={tooltipIcon}
                        tabIndex={0}
                        aria-describedby="start_over"
                    />
                </TooltipHost>
            </div>
        </div>
    );
}
